import { frappeList, frappeGet } from "./utils";
import { getERPClient } from "./client";
import type { FrappeListResponse } from "@/lib/frappe-client";
import type { FrappeFilterArray } from "./filterMapper";
import { docTypeListWithPagination } from "./docs";
import type { ItemPrice } from "./types/item";

/**
 * Default fields to fetch for Item Price list views
 *
 * This constant ensures consistency across:
 * - itemPrices.list() (legacy, if needed)
 * - itemPrices.listWithPagination() defaultFields
 * - useItemPriceListWithPagination fields
 * - createItemPriceColumns accessorKeys
 *
 * IMPORTANT:
 * - If you add a new column to createItemPriceColumns, add the corresponding field here.
 * - Only include real database fields (not computed fields).
 */
export const ITEM_PRICE_LIST_FIELDS = [
  "name",
  "item_code",
  "price_list",
  "price_list_rate",
  "currency",
  "uom",
  "valid_from",
  "valid_upto",
  "buying",
  "selling",
  "batch_no",
  "customer",
  "supplier",
  "packing_unit",
  "pack_qty",
] as const;

export interface ItemPriceListParams {
  /**
   * Pre-mapped Frappe filter array, e.g.:
   * [["Item Price", "item_code", "=", "ITEM-0001"]]
   */
  filters?: FrappeFilterArray;
  pageIndex?: number;
  pageSize?: number;
  orderBy?: string;
  fields?: string[];
}

export const itemPrices = {
  /**
   * List all item prices (legacy method - returns array only)
   * @deprecated Use listWithPagination for server-side pagination
   */
  list(filters?: Record<string, any>): Promise<ItemPrice[]> {
    return frappeList<ItemPrice>(
      "Item Price",
      filters,
      [...ITEM_PRICE_LIST_FIELDS]
    );
  },

  /**
   * List item prices with server-side pagination and filtering.
   *
   * This uses the generic docTypeListWithPagination path without
   * any special logic.
   */
  listWithPagination(
    params: ItemPriceListParams
  ): Promise<FrappeListResponse<ItemPrice>> {
    const {
      filters = [],
      pageIndex = 0,
      pageSize = 20,
      orderBy,
      fields,
    } = params;

    const defaultFields = fields ?? [...ITEM_PRICE_LIST_FIELDS];

    return docTypeListWithPagination<ItemPrice>({
      doctype: "Item Price",
      filters,
      pageIndex,
      pageSize,
      orderBy: orderBy ?? "modified desc",
      fields: defaultFields,
    });
  },

  /**
   * Get a single item price by name
   */
  detail(name: string): Promise<ItemPrice> {
    return frappeGet<ItemPrice>("Item Price", name);
  },

  /**
   * Create a new item price
   */
  create(data: Record<string, any>): Promise<ItemPrice> {
    const client = getERPClient();
    return client.create({ doctype: "Item Price", data });
  },

  /**
   * Update an existing item price
   */
  update(name: string, data: Record<string, any>): Promise<ItemPrice> {
    const client = getERPClient();
    return client.update({ doctype: "Item Price", name, data });
  },

  /**
   * Delete an item price
   */
  delete(name: string): Promise<void> {
    const client = getERPClient();
    return client.delete({ doctype: "Item Price", name });
  },
};

